import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import { ConfirmDialog } from "molecules/dialog/ConfirmDialog";
import {
	useLoadedCompanyDomain,
	useLoadedCompanyDomains,
	useLoadedLegalEntities,
} from "./routeDataHooks.js";
import {
	isScopeSwitchNavigation,
	scopeChangeNeedsConfirmation,
	scopeForPathname,
} from "./scopeChange.js";
import { useHasUnsavedChanges } from "./unsavedChanges.js";

export const ScopeChangeGuard = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const companyDomains = useLoadedCompanyDomains();
	const legalEntities = useLoadedLegalEntities();

	const unsavedChanges = useHasUnsavedChanges();

	const blocker = useBlocker(
		({ currentLocation, historyAction, nextLocation }) => {
			if (unsavedChanges?.current) return true;
			// WHY not back/forward: it undoes a choice the user already made.
			if (historyAction === "POP") return false;
			if (nextLocation.pathname === currentLocation.pathname) return false;
			if (isScopeSwitchNavigation(nextLocation.state)) return false;
			const change = scopeChangeNeedsConfirmation({
				fromPathname: currentLocation.pathname,
				toPathname: nextLocation.pathname,
			});
			if (!change) return false;
			if (
				change.companyDomainId === companyDomain.id &&
				legalEntities.length <= 1
			) {
				return false;
			}
			return true;
		},
	);

	if (blocker.state !== "blocked") {
		return null;
	}

	if (unsavedChanges?.current) {
		return (
			<ConfirmDialog
				open
				onClose={() => blocker.reset?.()}
				title={t("Discard unsaved changes?")}
				description={t(
					"You have unsaved role changes. Leaving this page now will discard them without saving.",
				)}
				cancelLabel={t("Keep editing")}
				confirmButtonLabel={t("Discard changes and leave page")}
				destructive
				onConfirm={() => blocker.proceed?.()}
			/>
		);
	}

	const target = scopeForPathname(blocker.location.pathname);
	const targetDomain = companyDomains.find(
		(domain) => `${domain.id}` === target?.companyDomainId,
	);
	const targetLegalEntity = legalEntities.find(
		(legalEntity) => legalEntity.id === target?.legalEntityId,
	);
	const changesDomain = target?.companyDomainId !== companyDomain.id;
	const leavesCompanyForDomain = !changesDomain && !target?.legalEntityId;

	const description = changesDomain
		? t("The page you are opening belongs to {{domain}}.", {
				domain: targetDomain?.name ?? t("another domain"),
			})
		: leavesCompanyForDomain
			? t("This page works on the domain level, across every company.")
			: t("The page you are opening belongs to {{company}}.", {
					company: targetLegalEntity?.name ?? t("another company"),
				});

	return (
		<ConfirmDialog
			open
			onClose={() => blocker.reset?.()}
			title={
				leavesCompanyForDomain
					? t("Switch to All companies?")
					: t("Switch company?")
			}
			description={`${description} ${t("Your current selection will change.")}`}
			confirmButtonLabel={leavesCompanyForDomain ? t("Continue") : t("Switch")}
			onConfirm={() => blocker.proceed?.()}
		/>
	);
};
